import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, Select } from '@/backoffice/components/ui';
import { useBackofficeRole } from '@/backoffice/hooks/useBackofficeRole';
import {
  loadClientSettings,
  saveClientSettings,
  type ClientSettings,
} from '@/domains/client/services/client.service';
import { Bell, Globe, Loader2, Save, Shield } from 'lucide-react';
import { cn } from '@/utils/cn';

const STORAGE_KEY = 'onapila_backoffice_settings';

interface GenericSettings {
  language: string;
  email_notifications: boolean;
  sms_notifications: boolean;
  marketing_emails: boolean;
}

function ToggleRow({ label, description, checked, onChange }: {
  label: string; description: string; checked: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-4 border-b border-gray-100 last:border-0">
      <div>
        <p className="font-medium text-gray-900">{label}</p>
        <p className="text-sm text-gray-500 mt-0.5">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={cn('relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors', checked ? 'bg-teal-600' : 'bg-gray-200')}
      >
        <span className={cn('inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-0.5', checked ? 'translate-x-5' : 'translate-x-0.5')} />
      </button>
    </div>
  );
}

export function SharedSettingsPage() {
  const userType = useBackofficeRole();
  const isClient = userType === 'client';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clientSettings, setClientSettings] = useState<ClientSettings | null>(null);
  const [generic, setGeneric] = useState<GenericSettings>({
    language: 'fr',
    email_notifications: true,
    sms_notifications: true,
    marketing_emails: false,
  });

  useEffect(() => {
    if (isClient) {
      loadClientSettings()
        .then(setClientSettings)
        .finally(() => setLoading(false));
    } else {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setGeneric(JSON.parse(raw) as GenericSettings);
      setLoading(false);
    }
  }, [isClient]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isClient && clientSettings) {
        await saveClientSettings(clientSettings);
      } else {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(generic));
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout userType={userType} title="Paramètres">
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" /></div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userType={userType} title="Paramètres">
      <div className="grid lg:grid-cols-2 gap-6 max-w-4xl">
        <Card>
          <h3 className="font-semibold flex items-center gap-2 mb-4"><Globe className="w-5 h-5" /> Langue</h3>
          <Select
            label="Langue de l'interface"
            value={isClient ? clientSettings?.language ?? 'fr' : generic.language}
            onChange={(e) => {
              if (isClient && clientSettings) setClientSettings({ ...clientSettings, language: e.target.value });
              else setGeneric((g) => ({ ...g, language: e.target.value }));
            }}
            options={[
              { value: 'fr', label: 'Français' },
              { value: 'en', label: 'English' },
            ]}
          />
        </Card>

        <Card>
          <h3 className="font-semibold flex items-center gap-2 mb-2"><Bell className="w-5 h-5" /> Notifications</h3>
          <ToggleRow
            label="Notifications email"
            description="Recevoir les alertes par email"
            checked={isClient ? clientSettings?.email_notifications ?? true : generic.email_notifications}
            onChange={(v) => {
              if (isClient && clientSettings) setClientSettings({ ...clientSettings, email_notifications: v });
              else setGeneric((g) => ({ ...g, email_notifications: v }));
            }}
          />
          <ToggleRow
            label="Notifications SMS"
            description="Alertes importantes par SMS"
            checked={isClient ? clientSettings?.sms_notifications ?? true : generic.sms_notifications}
            onChange={(v) => {
              if (isClient && clientSettings) setClientSettings({ ...clientSettings, sms_notifications: v });
              else setGeneric((g) => ({ ...g, sms_notifications: v }));
            }}
          />
        </Card>

        <Card className="lg:col-span-2">
          <h3 className="font-semibold flex items-center gap-2 mb-2"><Shield className="w-5 h-5" /> Sécurité</h3>
          <p className="text-sm text-gray-500">Pour modifier votre mot de passe, contactez le support ou utilisez la procédure de réinitialisation.</p>
        </Card>
      </div>

      <div className="mt-6">
        <Button leftIcon={<Save className="w-4 h-4" />} isLoading={saving} onClick={handleSave}>
          Enregistrer les paramètres
        </Button>
      </div>
    </DashboardLayout>
  );
}
